/**
 * TO-01 — the canned document frames.
 *
 * These are the "standard section set … the recognised frame, not an improvised
 * one" the specification asks for. Held as DATA, not produced by the model, which
 * is the whole point of TO-01: a litigation or transaction user asking for an NDA
 * template expects the same frame every time, and a model improvises a slightly
 * different one on each run.
 *
 * **Deliberately structure only.** Every entry is section headings plus
 * `[Square Brackets]` placeholders — there is NO substantive legal wording here,
 * and none should be added by an engineer. The specification puts the legal
 * content of these frames out of scope (Appendix A: "whether an NDA skeleton
 * contains the right clauses is a matter for the firm's precedent owners"), and a
 * developer-invented clause is worse than an empty placeholder precisely because
 * it looks authoritative. If a precedent owner supplies real content, it replaces
 * the bracketed lines — the headings stay.
 *
 * Markdown rather than a structured type on purpose: `parseMdBlocks` already
 * converts `#` / `##` / `###` into real Word Heading styles on insert, so these
 * frames reach the navigation pane through the same routine every other Word
 * insertion uses. No second insertion path to keep in step (W41).
 */

/** The document types the specification names for TO-01. */
export type TemplateKey =
  | "nda"
  | "msa"
  | "engagement_letter"
  | "power_of_attorney"
  | "side_letter"
  | "legal_memo";

export interface TemplateSkeleton {
  /** Shown on the pill: "Insert NDA template (9)". */
  readonly label: string;
  /** Markdown; `#` becomes the title, `##`/`###` the sections. */
  readonly markdown: string;
}

const NDA = `# Non-Disclosure Agreement

[Parties, and the date this agreement is made]

## 1. Definitions
[Define Confidential Information, Purpose, Representatives, and the Term]

## 2. Purpose
[State why the information is being shared and the permitted use]

## 3. Confidentiality Obligations
[Set out the duty not to disclose, and the standard of care required]

## 4. Permitted Disclosures
[List the exceptions — Representatives, professional advisers, disclosure required by law]

## 5. Exclusions
[Information already public, independently developed, or lawfully received from a third party]

## 6. Return and Destruction
[What happens to the information when the Purpose ends or on request]

## 7. Term and Survival
[How long the obligations run, and which survive termination]

## 8. Remedies
[Injunctive relief and any agreed remedies for breach]

## 9. General
[Governing law, jurisdiction, notices, entire agreement, no waiver]
`;

const MSA = `# Master Services Agreement

[Parties, and the date this agreement is made]

## 1. Interpretation
[Definitions, and the order of precedence between this agreement and any Schedule]

## 2. Services
### 2.1 Scope
[The services to be provided, and the Schedule that describes them]
### 2.2 Performance Standards
[Skill, care, quality and personnel requirements]
### 2.3 Sub-contracting
[Whether sub-contracting is permitted, approvals, and liability for sub-contractors]

## 3. Charges and Payment
[Fees, invoicing, payment terms, expenses, and any indexation]

## 4. Term and Termination
[The term, renewal, termination rights, notice periods, and survival]

## 5. Intellectual Property
[Ownership of pre-existing and newly created materials, and licences granted]

## 6. Confidentiality and Data Protection
[Confidentiality obligations, and the data-protection roles of each party]

## 7. Liability and Indemnities
[Caps, exclusions, and any indemnities]

## 8. Notices
[How notices must be sent and when they take effect]

## 9. General
[Governing law, jurisdiction, assignment, entire agreement, variation]

## 10. Schedules
### Schedule 1 — Services Description
[The detailed scope and deliverables]
### Schedule 2 — Charges
[The fee structure and any rate card]
`;

const ENGAGEMENT_LETTER = `# Engagement Letter

[Addressee, and the date of this letter]

## 1. Scope of Engagement
[The matter, and precisely what work is and is not included]

## 2. Our Team
[Who will act, and who is responsible for the matter]

## 3. Fees and Disbursements
[Basis of charging, rates, estimates, and disbursements]

## 4. Billing and Payment
[Frequency of billing, payment terms, and interest on late payment]

## 5. Client Responsibilities
[Information and instructions the client must provide]

## 6. Confidentiality and Conflicts
[Confidentiality undertaking, and how conflicts are handled]

## 7. Data Protection
[How personal data will be handled, and the applicable privacy notice]

## 8. Termination
[Each party's right to end the engagement, and what happens to work in progress]

## 9. Complaints
[The complaints procedure and the relevant regulator]

## 10. Acceptance
[Signature block confirming acceptance of these terms]
`;

const POWER_OF_ATTORNEY = `# Power of Attorney

[Donor, attorney(s), and the date this deed is made]

## 1. Appointment
[Who is appointed, and whether attorneys act jointly or jointly and severally]

## 2. Powers Granted
[The specific powers conferred, and any expressly excluded]

## 3. Limitations and Conditions
[Monetary limits, required consents, and any conditions on exercise]

## 4. Duration
[When the power takes effect, and when it ends]

## 5. Revocation
[How the donor may revoke, and the notice required]

## 6. Attorney's Duties
[Standard of care, record-keeping, and conflicts]

## 7. Indemnity
[Any indemnity given to the attorney]

## 8. Governing Law
[Governing law and jurisdiction]

## 9. Execution
[Execution as a deed, with witness details]
`;

const SIDE_LETTER = `# Side Letter

[Parties, the date, and the principal agreement this letter varies]

## 1. Background
[The principal agreement, its date, and why it is being varied]

## 2. Agreed Variations
[Each variation, identified by the clause it amends]

## 3. Conditions
[Any conditions the variations depend on]

## 4. Duration
[Whether the variations are permanent or time-limited]

## 5. Effect on the Principal Agreement
[Confirmation that the principal agreement otherwise continues unchanged]

## 6. Confidentiality
[Whether the existence and terms of this letter are confidential]

## 7. Governing Law
[Governing law and jurisdiction]

## 8. Counterparts and Execution
[Signature blocks for each party]
`;

const LEGAL_MEMO = `# Legal Memorandum

[To, From, Date, and Subject]

## 1. Question Presented
[The precise legal question being answered]

## 2. Short Answer
[The conclusion, in two or three sentences]

## 3. Facts
[The facts relied on, and any that remain unconfirmed]

## 4. Analysis
### 4.1 Applicable Law
[Statute, regulation and authority relied on]
### 4.2 Application to the Facts
[How the law applies to these facts]
### 4.3 Counter-arguments
[The strongest arguments the other way, and why they do or do not succeed]

## 5. Risks and Assumptions
[Key risks, and the assumptions the analysis depends on]

## 6. Recommendation
[Recommended course of action and next steps]
`;

export const WORD_TEMPLATE_SKELETONS: Readonly<Record<TemplateKey, TemplateSkeleton>> = {
  nda: { label: "NDA", markdown: NDA },
  msa: { label: "MSA", markdown: MSA },
  engagement_letter: { label: "engagement letter", markdown: ENGAGEMENT_LETTER },
  power_of_attorney: { label: "power of attorney", markdown: POWER_OF_ATTORNEY },
  side_letter: { label: "side letter", markdown: SIDE_LETTER },
  legal_memo: { label: "legal memo", markdown: LEGAL_MEMO },
};

/** Section count for the pill label — headings only, stubs excluded. */
export function countTemplateSections(markdown: string): number {
  return (markdown.match(/^#{1,3} .+/gm) ?? []).length;
}

/** Whether a string names one of the six frames. */
export function isTemplateKey(value: string): value is TemplateKey {
  return Object.prototype.hasOwnProperty.call(WORD_TEMPLATE_SKELETONS, value);
}
